/**
 * Fotoja para se të niset — zvogëlimi që e mban serverin të lehtë.
 *
 * Ruajtja nuk ndryshon: skedari shkon te e njëjta rrugë e ngarkimit si fotoja e
 * profilit dhe si çdo bashkëngjitje tjetër — në disk, jashtë web-root, i
 * shërbyer vetëm nga një rrugë me leje. Ajo që ndryshon është SA dërgohet.
 *
 * Arsyeja është importi masiv: dyqind foto të marra drejt e nga telefoni janë
 * 3–8 MB secila, pra deri në një gigabajt e gjysmë përmes një lidhjeje zyre —
 * për fotografi që në ekran shihen 92 piksele të gjera. Një portret te profili
 * e ka të njëjtin defekt, vetëm se atje është një skedar dhe këtu janë njëqind.
 *
 * Rregullat janë të gjitha "mos bëj dëm":
 *
 *   • skedari nën 300 KB nuk preket — s'ka çka të fitohet;
 *   • ajo që nuk është JPG/PNG/WEBP kalon ashtu siç është;
 *   • nëse shfletuesi nuk e dekodon dot (ose `canvas` mungon, si te testet),
 *     kthehet ORIGJINALI — një foto e padërguar do të ishte dëm më i madh se
 *     një foto e madhe;
 *   • nëse rezultati del më i madh se origjinali (ndodh me PNG-të e vogla me
 *     tekst), mbahet origjinali.
 *
 * Kthimi është gjithmonë një `File` i gatshëm për `FormData`.
 */

/** Ana më e gjatë e fotos pas zvogëlimit. 1600 px mjafton për ekran dhe për printim të vogël. */
const MAX_SIDE = 1600;
/** Cilësia e JPEG-ut. Nën 0.8 duket artefakti te fotot e pajisjeve me tekst (etiketa, seri). */
const QUALITY = 0.82;
/** Nën këtë madhësi, skedari niset ashtu siç është. */
const SKIP_UNDER = 300 * 1024;

const SHRINKABLE = /^image\/(jpeg|png|webp)$/i;

/** Emri me prapashtesën e re — `laptop.png` → `laptop.jpg`. */
const asJpgName = (name) => String(name || 'foto').replace(/\.[a-z0-9]{2,5}$/i, '') + '.jpg';

/**
 * Skedar → bitmap, me rrugën e re dhe me atë të vjetrën.
 *
 * `createImageBitmap` është e shpejtë dhe nuk e prek DOM-in, por mungon te
 * Safari-t e vjetër; aty bie te `Image` me një `objectURL` që lirohet gjithsesi.
 * Kthen `null` kur asnjëra nuk punon — dhe atëherë thirrësi dërgon origjinalin.
 */
async function decode(file) {
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(file); } catch (_) { /* provo rrugën e vjetër */ }
  }
  if (typeof Image !== 'function' || typeof URL?.createObjectURL !== 'function') return null;
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('decode'));
      img.src = url;
    });
  } catch (_) {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

const sizeOf = (bitmap) => ({
  w: bitmap.width || bitmap.naturalWidth || 0,
  h: bitmap.height || bitmap.naturalHeight || 0,
});

/**
 * Zvogëlon një foto para ngarkimit. Kurrë nuk hedh gabim: çdo dështim kthen
 * skedarin origjinal, sepse detyra e tij është të dërgohet, jo të jetë i vogël.
 *
 * @param {File} file
 * @param {{maxSide?: number, quality?: number, skipUnder?: number}} [opts]
 * @returns {Promise<File>}
 */
export async function shrinkImage(file, opts = {}) {
  const { maxSide = MAX_SIDE, quality = QUALITY, skipUnder = SKIP_UNDER } = opts;
  if (!file || !SHRINKABLE.test(file.type || '') || file.size <= skipUnder) return file;

  try {
    const bitmap = await decode(file);
    if (!bitmap) return file;
    const { w, h } = sizeOf(bitmap);
    if (!w || !h) return file;

    const scale = Math.min(1, maxSide / Math.max(w, h));
    const canvas = document.createElement('canvas');
    if (!canvas.getContext) return file;
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    if (typeof bitmap.close === 'function') bitmap.close();

    const blob = await new Promise((resolve) => {
      // `toBlob` mungon te jsdom-i; pa këtë kontroll testet do të rrinin pezull.
      if (typeof canvas.toBlob !== 'function') { resolve(null); return; }
      try { canvas.toBlob(resolve, 'image/jpeg', quality); } catch (_) { resolve(null); }
    });
    // Një JPEG më i madh se origjinali nuk ka pse të dërgohet.
    if (!blob || blob.size >= file.size) return file;

    return new File([blob], asJpgName(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified || Date.now(),
    });
  } catch (_) {
    return file;
  }
}

/** Sa MB janë këta skedarë — vetëm për ta thënë në ekran. */
export const totalMb = (files = []) =>
  Math.round((files.reduce((s, f) => s + (f?.size || 0), 0) / 1048576) * 10) / 10;

export const IMAGE_DEFAULTS = { MAX_SIDE, QUALITY, SKIP_UNDER };
